import { defineStore } from 'pinia';
import axios from 'axios';

export const useUserStore = defineStore('user', {
  state: () => ({
    user: null,          // { id, nume, tip_cont, ... }
    firmaCurenta: null,  // { id }  (server return is just the id)
    loaded: false,
    sidebarViewMode: (typeof localStorage !== 'undefined' ? localStorage.getItem('sidebar_view_mode') : null) || 'tot', // 'client' | 'contabil' | 'tot'
  }),

  getters: {
    isLogged: (s) => !!s.user?.id,
    isAdmin:  (s) => String(s.user?.tip_cont ?? '').toLowerCase() === 'admin' || s.user?.tip_cont === 1,
    firmaId:  (s) => (typeof s.firmaCurenta === 'number' ? s.firmaCurenta : s.firmaCurenta?.id ?? null),
    currentSidebarMode: (s) => s.sidebarViewMode || 'tot',
  },

  actions: {
    async fetchMe(opts = {}) {
      const { redirectOn401 = false, silent401 = true } = opts;
      try {
        // Evită apelul pe pagina de login
        if (typeof window !== 'undefined') {
          const p = window.location?.pathname || '';
          if (p === '/login' || p.startsWith('/login/')) {
            this.user = null;
            this.firmaCurenta = null;
            return;
          }
        }

        const { data } = await axios.get('/api/cont', {
          // withCredentials: true, // deblochează dacă API-ul cere cookie (Sanctum)
          validateStatus: (s) => (s >= 200 && s < 300) || s === 401 || s === 419,
        });

        if (!data || data.message === 'Unauthenticated.') {
          // Echivalent 401 din API
          this.user = null;
          this.firmaCurenta = null;
          if (redirectOn401) {
            if (typeof window !== 'undefined') window.location = '/login';
          }
          return;
        }

        this.user = {
          ...data.user,
          firma_curenta: data.firma_curenta
        };

        const fcRaw = data.firma_curenta ?? null;
        let tipEntitate = null;
        let tipContabilitate = null;

        if (fcRaw && typeof fcRaw === 'object') {
          tipEntitate = fcRaw.tip_entitate ?? null;
          tipContabilitate = fcRaw.tip_contabilitate ?? null;
          const denumireUpper = (fcRaw.denumire ?? '').toUpperCase();
          if (tipEntitate === 'PFA' || denumireUpper.includes('ÎNTREPRINDERE INDIVIDUALĂ') || denumireUpper.includes('INTREPRINDERE INDIVIDUALA') || denumireUpper.includes(' I.I.') || denumireUpper.includes(' PFA') || denumireUpper.includes(' I.F.') || denumireUpper.includes(' C.M.I.') || denumireUpper.includes(' P.F.A.')) {
            tipContabilitate = 'SIMPLIFICATA';
            fcRaw.tip_contabilitate = 'SIMPLIFICATA';
          }
        }

        this.firmaCurenta = (fcRaw && typeof fcRaw === 'object')
          ? {
              id: fcRaw.id,
              denumire: fcRaw.denumire ?? '',
              cui: fcRaw.cui ?? '',
              stare_firma: fcRaw.stare_firma ?? null,
              tip_entitate: tipEntitate,
              tip_contabilitate: tipContabilitate,
              scp_tva: fcRaw.scp_tva !== undefined ? fcRaw.scp_tva : null
            }
          : (typeof fcRaw === 'number' || typeof fcRaw === 'string')
            ? { id: Number(fcRaw), cui: '', stare_firma: null, tip_entitate: null, tip_contabilitate: null, scp_tva: null }
            : null;

        if (window && window.$gate) {
          window.$gate.tip_cont = this.user?.tip_cont ?? null;
          window.$gate.firma_curenta = this.firmaCurenta;
          window.$gate.tip_entitate = tipEntitate;
          window.$gate.tip_contabilitate = tipContabilitate;
        }
      } catch (e) {
        const status = e?.response?.status;
        if ((status === 401 || status === 419) && silent401) {
          // starea neautentificat: păstrăm tăcere, doar resetăm store-ul
          this.user = null;
          this.firmaCurenta = null;
        } else {
          console.warn('fetchMe error:', e);
          this.user = null;
          this.firmaCurenta = null;
        }
      } finally {
        this.loaded = true;
      }
    },

    // setează explicit (de ex. după schimbarea firmei)
    setUser(user) {
      this.user = user || null;
    },
    setFirmaCurenta(firma) {
      this.firmaCurenta = (firma && typeof firma === 'object')
        ? firma
        : (typeof firma === 'number' || typeof firma === 'string')
          ? { id: Number(firma) }
          : null;
    },

    setSidebarViewMode(mode) {
      this.sidebarViewMode = mode;
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('sidebar_view_mode', mode);
        }
      } catch (e) {}
    },

    // logout / reset store rapid
    clear() {
      this.user = null;
      this.firmaCurenta = null;
      this.loaded = false;
    },

    // fallback temporar dacă încă folosești meta tags
    hydrateFromMeta() {
      try {
        if (typeof document === 'undefined') {
          this.loaded = true;
          return;
        }
        const get = (n) => document.querySelector(`meta[name="${n}"]`)?.content;
        const id  = get('user_id');
        const tip = get('tip_cont');
        const fid = get('firma_curenta');

        if (id)  this.user = { ...(this.user || {}), id: Number(id), tip_cont: tip };
        if (fid) this.firmaCurenta = { ...(this.firmaCurenta || {}), id: Number(fid) };
      } catch (e) {
        console.warn('hydrateFromMeta warn:', e);
      } finally {
        this.loaded = true;
      }
    },
  },

  // Dacă vrei persistență automată în localStorage:
  // persist: true,
});